import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { RbacService } from '../services/rbac/rbac.service';

function redirectAuthenticatedUser(
  rbacService: RbacService,
  router: Router,
  requestedUrl?: string | null
): void {
  const target = requestedUrl ?? rbacService.getDefaultRoute();
  const destination = rbacService.canAccessRoute(target)
    ? target
    : rbacService.getDefaultRoute();
  const currentPath = router.url.split('?')[0];

  if (currentPath !== destination) {
    router.navigate([destination], { replaceUrl: true });
  }
}

export const LoginGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const rbacService = inject(RbacService);
  const router = inject(Router);
  const path = state.url.split('?')[0];

  if (path === '/login2') {
    return true;
  }

  const hasSsoCallback = authService.hasSsoCallback();

  if (hasSsoCallback) {
    if (!authService.handleAuthCallback()) {
      router.navigate(['/login'], { replaceUrl: true });
      return false;
    }
  }

  if (!hasSsoCallback && !authService.isAuthenticated()) {
    return true;
  }

  const loadContext$ = hasSsoCallback
    ? authService.exchangeSsoToken().pipe(
        switchMap((exchanged) => {
          if (!exchanged) {
            router.navigate(['/login'], { replaceUrl: true });
            return of(false);
          }
          return rbacService.ensureContextLoaded().pipe(map(() => true));
        })
      )
    : rbacService.ensureContextLoaded().pipe(map(() => true));

  return loadContext$.pipe(
    map((ready) => {
      if (!ready) {
        return false;
      }
      const requested = hasSsoCallback
        ? authService.consumePostLoginRedirectUrl()
        : null;
      redirectAuthenticatedUser(rbacService, router, requested);
      return false;
    })
  );
};
